/* eslint-disable @typescript-eslint/camelcase */
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import store from "@/store";
import {
  BagOrSttReadyToCargo,
  ReadyToCargo,
  RequestLogReadyToCargoList,
  RequestReadyToCargoList,
  ResponseScanReadyToCargo,
  ResponseUpdateReadyToCargo,
  ScanReadyToCargoRequest,
  UpdateReadyToCargoRequest
} from "@/domain/entities/ReadyToCargo";
import { ReadyToCargoPresenter } from "../presenters/ReadyToCargoPresenter";
import { container } from "tsyringe";
import { ReadyToCargoComponent } from "@/app/infrastructures/dependencies/modules/ReadyToCargoComponent";
import { MainAppController } from "./MainAppController";
import {
  formatDateNumber,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import router from "../router";
import { playNotification } from "@/app/infrastructures/misc/UtilsAudio";
import { BagOrSttNumber, FlightPlanForECargo, DimensionPieces } from "@/data/payload/api/CargoApiRequest";
import { CargoController } from "./CargoController";
import { OptionsClass } from "@/domain/entities/MainApp";
import { CargoInputData, DestinationCityData, DimensionData, OriginCityData, DimensionPerKoliDetail, DimensionPerKoliData, CargoScanFailed, CalculateVolume } from "@/domain/entities/Cargo";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { FlightDetail } from "@/domain/entities/NGen";
import { flags } from "@/feature-flags";
import { FlagsPermissionCargo } from '@/feature-flags/flags-permission-cargo';
import { storeDatatoSessionStorage } from "@/app/infrastructures/misc/SessionStorage";
import { getDataSessionStorage } from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { getConfigDimensionDivider, CALCULATE_TYPE } from "../views/cargo/modules/cargo-module";
import { FlagRtcSeamless } from "@/feature-flags/flags-rtc-seamless";

ReadyToCargoComponent.init();

export interface ReadyToCargoState {
  readyToCargoDetail: ReadyToCargo;
}
@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "ready-to-cargo"
})
class ReadyToCargoStore extends VuexModule implements ReadyToCargoState {
  public readyToCargoDetail: ReadyToCargo = JSON.parse(
    getDataSessionStorage("rtc-detail") || "{}"
  );
  public bagSttDetail = new ResponseScanReadyToCargo();
  public sourceSttFromLocalStorage: BagOrSttReadyToCargo[] = JSON.parse(
    getDataSessionStorage("stt-data") || "[]"
  );
  public bagResult: {
    bagNo: string;
    stt: BagOrSttReadyToCargo[];
  }[] = JSON.parse(getDataSessionStorage("bag-data") || "[]");
  public flowBooking: string = JSON.parse(
    getDataSessionStorage("flow-booking") || "{}"
  );
  public eventName: string = JSON.parse(
    getDataSessionStorage("event-name") || "{}"
  );
  
  public detailFlightsData: FlightDetail[] = JSON.parse(
    getDataSessionStorage("rtc-flights-data") || "[]"
  ); 

  public detailNextFlightsData: FlightDetail[] = JSON.parse(
    getDataSessionStorage("next-rtc-flights-data") || "[]"
  ); 

  public calculateFlightDate: FlightPlanForECargo[] = JSON.parse(
    getDataSessionStorage("calculate-flight-date") || "[]"
  );
  public scanFailedList: CargoScanFailed[] = [];

  get isScanFlagV4Enabled() {
    return FlagRtcSeamless.permission_rtc_seamless_scan_flag_v4_enable.isEnabled();
  }

  get sttResultFinal() {
    const tempData: BagOrSttReadyToCargo[] = [];
    if (this.sourceSttFromLocalStorage) {
      this.sourceSttFromLocalStorage.forEach((e: BagOrSttReadyToCargo) => {
        const existingIdx = tempData.findIndex(
          (a: BagOrSttReadyToCargo) => a.bagNo === e.bagNo
        );
        if (e.bagNo && !e.sttDetails.length) {
          if (existingIdx > -1) {
            tempData[existingIdx]["sttDetails"].push(e);
          } else {
            tempData.push({ ...e, sttDetails: [e] });
          }
          return;
        }
        tempData.push(e);
      });
    }
    return tempData;
  }

  get totalSttPieces() {
    // STTs with the same bagNo counted as one, STTs without bagNo counted per totalPieces
    return this.sttResultFinal
      .map((key: BagOrSttReadyToCargo) =>
        key.bagNo && key.sttDetails.length ? 1 : key.sttTotalPiece
      )
      .reduce((a, b) => a + b, 0);
  }

  get totalGrossWeight() {
    return this.sourceSttFromLocalStorage
      .map((key: BagOrSttReadyToCargo) => key.grossWeight)
      .reduce((a, b) => {
        return a + b;
      }, 0);
  }

  get totalVolumeWeight() {
    return this.sourceSttFromLocalStorage
      .map((key: BagOrSttReadyToCargo) => key.volumeWeight)
      .reduce((a, b) => {
        return a + b;
      }, 0);
  }

  @Action
  public getReadyToCargoList(params: RequestReadyToCargoList) {
    params.hubId = +getHubData()?.hubId;
    const presenter = container.resolve(ReadyToCargoPresenter);
    return presenter.getReadyToCargoList(params);
  }

  @Action
  public getLogReadyToCargoList(params: RequestLogReadyToCargoList) {
    params.hubId = +getHubData()?.hubId;
    const presenter = container.resolve(ReadyToCargoPresenter);
    return presenter.getLogReadyToCargoList(params);
  }

  @Action
  private _calculateVolumeWeight(dimension: {
    length: number;
    width: number;
    height: number;
    divider: number;
  }): number {
    const volumeWeight =
      (dimension.length * dimension.width * dimension.height) /
      dimension.divider;

    return volumeWeight !== 0 ? volumeWeight : 0;
  }

  @Action
  private _setRTCDimensionPieces(data: ReadyToCargo) {
    let pieces: DimensionPieces[] = [];
    let piecesNew: DimensionPerKoliDetail[] = [];
    const isPlane = data.cargoType === "plane";

    const divider = getConfigDimensionDivider(data.cargoType);

    const calculateVolume = (dimension: { length: number; width: number; height: number; volume: any }) => {
      const volumeWeight = (dimension.length * dimension.width * dimension.height) / divider;
      return volumeWeight || 0;
    }
    data.detail.forEach(stt => {
      if (stt.baggingNumber) {
        pieces.push(
          new DimensionPieces({
            width: stt.bagCustomWidth, height: stt.bagCustomHeight, length: stt.bagCustomLength,
            volumeWeight: calculateVolume({ width: stt.bagCustomWidth, length: stt.bagCustomLength, height: stt.bagCustomHeight, volume: stt.bagCustomVolumeWeight}),
            sttOrBag: stt.baggingNumber,
            grossWeight: stt.bagCustomGrossWeight,
            pieces: 1
          })
        )
        piecesNew.push(
          new DimensionPerKoliDetail({
            totalPieces: 1,
            sttPieceWidth: stt.bagCustomWidth,
            sttPieceVolumeWeight: calculateVolume({ width: stt.bagCustomWidth, length: stt.bagCustomLength, height: stt.bagCustomHeight, volume: stt.bagCustomVolumeWeight}),
            sttPieceLength: stt.bagCustomLength, sttPieceHeight: stt.bagCustomHeight, sttPieceGrossWeight: stt.bagCustomGrossWeight, sttOrBag: stt.baggingNumber
          })
        )
      } else {
        stt.sttPieces.forEach((piece, index) => {
          pieces.push(new DimensionPieces({
            width: piece.sttPieceWidth,
            volumeWeight: calculateVolume({ width: piece.sttPieceWidth, length: piece.sttPieceLength, height: piece.sttPieceHeight, volume: piece.sttPieceVolumeWeight }),
            grossWeight: piece.sttPieceGrossWeight,
            height: piece.sttPieceHeight,
            length: piece.sttPieceLength,
            sttOrBag: `${stt.sttNumber} #${index + 1}`,
            pieces: 1
          }))
          piecesNew.push(
            new DimensionPerKoliDetail({
              totalPieces: 1,
              sttPieceWidth: piece.sttPieceWidth,
              sttPieceVolumeWeight: calculateVolume({ width: piece.sttPieceWidth, length: piece.sttPieceLength, height: piece.sttPieceHeight, volume: piece.sttPieceVolumeWeight }),
              sttPieceLength: piece.sttPieceLength,
              sttPieceHeight: piece.sttPieceHeight,
              sttPieceGrossWeight: piece.sttPieceGrossWeight,
              sttOrBag: `${stt.sttNumber} #${index + 1}`,
            })
          )
        })
      }
    })
    const bag = new Set();
    const newBag = new Set();
    pieces = pieces.filter(piece => !bag.has(piece.sttOrBag) && bag.add(piece.sttOrBag))
    piecesNew = piecesNew.filter(piece => !newBag.has(piece.sttOrBag) && newBag.add(piece.sttOrBag))
    if (
      [
        pieces.length,
        !flags.feature_hide_dimension_koli.isEnabled(),
        isPlane
      ].every((p) => p)
    ) {
      CargoController.setHaveBeenSubmit(true);
      CargoController.setFormDetailPieces({ totalPieces: pieces.length, dimensionPieces: pieces })
      CargoController.setDimensionPerKoliData(new DimensionPerKoliData({ totalPieces: piecesNew.length, dimensionPieces: piecesNew }))
    }
  }

  @Action _setRTCDimensionData(data: ReadyToCargo, flow?: string) {
    const dimension = {
      length: 90,
      width: 60,
      height: 0
    };
    if (flow == "rtc-edit") {
      dimension.length = 60;
      dimension.height = 0;
    }
    Object.assign(CargoController.form, {
      actualTotalCargoGrossWeight: data.totalWeight.toString(),
      actualDimension: new DimensionData({
        length: dimension.length,
        width: 60,
        height: dimension.height
      }),
      cargoType: data.cargoType,
      cargoTypeStatus: data.cargoType.match(/plane/gi) ? "auto" : "manual",
      cargoCommodityName:
        CargoController.form.cargoCommodityName || data.commodityName,
      cargoCommodityCode:
        CargoController.form.cargoCommodityCode || data.commodityCode,
      actualTotalSTTPieces: data.totalPieces,
      destinationCityCode: this.readyToCargoDetail.destinationCode,
      destinationCityName: this.readyToCargoDetail.destinationName,
      flightsData: this.detailFlightsData,
      nextFlightsData: this.detailNextFlightsData
    });

    // save data to cargo-input-data localstorage when open rtc proccess or rtc edit
    const cargoInputForm: CargoInputData = JSON.parse(
      getDataSessionStorage("cargo-input-data") || "{}"
    );
    const newCargoInputData = new CargoInputData({
      id: data.id,
      dimension: new DimensionData({
        length: dimension.length,
        width: 60,
        height: dimension.height
      }),
      totalActualCargoGrossWeight: data.totalWeight,
      calcuateActualCargoGrossWeight: data.totalWeight, // explanation https://github.com/Lionparcel/medusa/pull/5753/files#r1285584448
      totalActualCargoVolumeWeight: data.totalPieces,
      totalActualCargoColi: data.totalPieces,
      destinationCityData: new DestinationCityData({
        name: this.readyToCargoDetail.destinationName,
        code: this.readyToCargoDetail.destinationCode,
        label: `${this.readyToCargoDetail.destinationCode} - ${this.readyToCargoDetail.destinationName}`
      }),
      originCityData: new OriginCityData({
        code: data.originCode,
        name: data.originName
      }),
      nog: "",
      cargoComodity: new OptionsClass({
        name: cargoInputForm.cargoComodity?.name,
        code: cargoInputForm.cargoComodity?.code
      }),
      vehicleNumber: "",
      cargoNumber: "",
      flightData: this.detailFlightsData
    });
    storeDatatoSessionStorage("cargo-input-data", newCargoInputData);
    CargoController.setInitCalculateVolumeData(new CalculateVolume({
      chargeableWeight: data.currentChargeableWeight,
      calculateType: CALCULATE_TYPE.INIT
    }));
  }

  @Action
  public getDetailReadyToCargo(id: number, flow?: string) {
    MainAppController.showLoading();
    const presenter = container.resolve(ReadyToCargoPresenter);
    return new Promise((resolve, reject) => {
    presenter
      .getDetailReadyToCargo(id)
      .then((key: ReadyToCargo) => {
        CargoController.setDestinationCity(
          new OptionsClass({
            code: key.destinationCode,
            label: `${key.destinationCode} - ${key.destinationName}`,
            name: key.destinationName
          })
        );
        this.setReadyToCargoDetail(key);
        this._setRTCDimensionPieces(key);
        if (key.nextFlightsData[0].departureHour) {
          const result = key.nextFlightsData.map((flightData: any) => {
            return new FlightDetail({
              ...flightData,
              DepartureDateTime: key.nextCargoFlightTime
            });
          });
          this.setDetailNextFlightData(result);
        } else {
          this.setDetailNextFlightData(key.nextFlightsData);
        }
        this._setRTCDimensionData(key, flow);
        const cargoData = {
          type: key.cargoType,
          status: key.cargoType == "plane" ? "auto" : "manual"
        };
        CargoController.setCargoType(cargoData);
        if (cargoData.type === "plane") {
          CargoController.setIsCargoDimensionOn(!flags.feature_hide_dimension_koli.isEnabled());
        } else {
          CargoController.setIsCargoDimensionOn(false);
        }
        router.push("/cargo/create");
        resolve(key);
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            err,
            "Gagal Mendapatkan Detail Siap di Kargo",
            () => {
              this.getDetailReadyToCargo(id);
              MainAppController.closeErrorMessage();
            }
          )
        );
        reject();
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
    })
  }

  @Action
  async setCargoData() {
    const tempData: any = [];
    if (this.readyToCargoDetail.detail) {
      this.readyToCargoDetail.detail.forEach((e: any) => {
        const existingIdx = tempData.findIndex(
          (a: any) => a.baggingNumber === e.baggingNumber
        );
        if (e.baggingNumber && !e.sttDetails) {
          if (existingIdx > -1) {
            tempData[existingIdx]["sttDetails"].push(e);
          } else {
            tempData.push({ ...e, sttDetails: [e] });
          }
          return;
        }
        tempData.push(e);
      });
    }
    storeDatatoSessionStorage("cargo", this.readyToCargoDetail.detail);
    CargoController.setSTTResultFinal(tempData);
    CargoController.setSourceFromLocalStorage(this.readyToCargoDetail.detail);
  }

  @Action
  public scanReadyToCargo(payload: {
    bagOrSttNumber: ScanReadyToCargoRequest;
    callback: Function;
  }) {
    const version = this.isScanFlagV4Enabled ? "v4" : "v1";
    MainAppController.showLoading();
    const presenter = container.resolve(ReadyToCargoPresenter);
    return presenter
      .scanReadyToCargo(
        payload.bagOrSttNumber,
        version
      )
      .then((res: ResponseScanReadyToCargo) => {
        this.setBagSttDetail(res);
        payload.callback(res);
        return true;
      })
      .catch(error => {
        playNotification("error");
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, "Tambah No. STT Gagal !", () => {
            MainAppController.closeErrorMessage();
            this.scanReadyToCargo(payload);
          })
        );

        const message = (error as any)?.response?.data?.message;

        if (typeof message !== "string") {
          const { stt_no, bag_no } = payload.bagOrSttNumber.bagOrSttNo;

          this.addScanFailedList(
            new CargoScanFailed({
              sttNumber: stt_no || bag_no,
              failureReason: parsingErrorResponse(error).message
            })
          );
        }

        return false;
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Action
  public updateReadyToCargo(isNewConfig = 0) {
    const version = this.isScanFlagV4Enabled ? "v4" : "v1";
    const payloadBagNoOnly = this.bagResult.map(
      (item: any) => new BagOrSttNumber(item.bagNo, "")
    );
    const payloadSttNoOnly = this.sourceSttFromLocalStorage
      .filter(
        (item: any) =>
          !this.bagResult.find((bag: any) =>
            JSON.stringify(bag).includes(item.sttNo)
          )
      )
      .map((item: any) => new BagOrSttNumber("", item.sttNo));
    const payload = new UpdateReadyToCargoRequest({
      bagOrSttNo: [...payloadSttNoOnly, ...payloadBagNoOnly],
      hubId: getHubData()?.hubId,
      hubName: getHubData()?.hubName,
      hubDistrictCode: getHubData()?.hubDistrictCode,
      hubOriginCity: getHubData()?.originCity,
      isNewConfig
    });
    MainAppController.showLoading();
    const presenter = container.resolve(ReadyToCargoPresenter);
    return presenter
      .updateReadyToCargo(payload, version)
      .then((res: ResponseUpdateReadyToCargo) => {
        return res;
      })
      .catch(error => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, "Pembuatan Gagal !", () => {
            MainAppController.closeErrorMessage();
            this.updateReadyToCargo();
          })
        );
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Mutation
  public setReadyToCargoDetail(obj: ReadyToCargo) {
    storeDatatoSessionStorage("rtc-detail", obj);
    this.readyToCargoDetail = obj;
  }

  @Mutation
  public setCalculateFlightDate(data: FlightPlanForECargo[]) {
    storeDatatoSessionStorage("calculate-flight-date", data);
    this.calculateFlightDate = data;
  }

  @Mutation
  public setDetailFlightData(data: FlightDetail[]) {
    storeDatatoSessionStorage("rtc-flights-data", data);
    this.detailFlightsData = data;
  }

  @Mutation
  public setDetailNextFlightData(data: FlightDetail[]) {
    storeDatatoSessionStorage("next-rtc-flights-data", data);
    this.detailNextFlightsData = data;
  }

  @Mutation
  public setFlowBooking(value: string) {
    storeDatatoSessionStorage("flow-booking", value);
    this.flowBooking = value;
  }

  @Mutation
  public setEventName(value: string) {
    storeDatatoSessionStorage("event-name", value);
    this.eventName = value;
  }

  @Mutation
  public setBagSttDetail(data: ResponseScanReadyToCargo) {
    this.bagSttDetail = data;
  }

  @Mutation
  public setSourceFromLocalStorage(data: BagOrSttReadyToCargo[]) {
    this.sourceSttFromLocalStorage = data;
  }

  @Mutation
  public setBagResult(data: any) {
    this.bagResult = data;
  }

  @Mutation
  public addScanFailedList(data: CargoScanFailed): void {
    const idx = this.scanFailedList.findIndex(
      (e) => e.sttNumber === data.sttNumber
    );

    if (data.success)
      return void (idx >= 0 && this.scanFailedList.splice(idx, 1));

    if (idx >= 0) this.scanFailedList.splice(idx, 1);
    this.scanFailedList.unshift(data);

    storeDatatoSessionStorage(
      "ready-for-cargo-scan-failed",
      this.scanFailedList
    );
  }

  @Mutation
  public resetScanFailedList(): void {
    storeDatatoSessionStorage("ready-for-cargo-scan-failed", []);
    this.scanFailedList = [];
  }

  @Mutation
  public getScanFailedListFromStorage(): void {
    this.scanFailedList = JSON.parse(
      getDataSessionStorage("ready-for-cargo-scan-failed") || "[]"
    );
  }
}

export const ReadyToCargoController = getModule(ReadyToCargoStore);
